// Requests here describe permissions only. They never authorize or execute an application action.
export function normalizeInspectionCheck(input) {
  if (!input || !input.core || input.id === undefined || input.id === null || input.id === '') return null
  if (['modules', 'page', 'showfields', 'modfields', 'fields', 'actions', 'tabs'].includes(input.core)
    && (!Number.isSafeInteger(Number(input.id)) || Number(input.id) < 1)) return null
  const check = { root: input.root || 'interface', core: String(input.core), id: input.id }
  if (input.created_by !== undefined) check.created_by = input.created_by
  if (input.parent_allowed !== undefined) check.parent_allowed = input.parent_allowed
  if (input.table) check.table = String(input.table)
  return check
}

export const inspectionCheckKey = input => {
  const check = normalizeInspectionCheck(input)
  return check ? JSON.stringify([check.root, check.core, String(check.id), check.created_by === undefined ? false : check.created_by,
    check.parent_allowed === undefined ? true : check.parent_allowed, check.table || '']) : ''
}

export function inspectionIdentity(user, authenticated = true) {
  return authenticated && user && Number(user.type) === 3 && user.public === false && user.uuid && user.profil_id && user.entreprise
    ? [user.uuid, user.profil_id, user.entreprise].map(String).join(':') : ''
}

export function createInspectionManager({ reactive = value => value, set = (object, key, value) => { object[key] = value }, request,
  storage = null, schedule = callback => setTimeout(callback, 30), cancel = timer => clearTimeout(timer) }) {
  const state = reactive({ identity: '', eligible: false, target: null, mode: 'annotations', viewGeneration: 0, transitioning: false, viewError: '', results: {}, error: '', loading: false })
  const registrations = new Map()
  let revision = 0, timer = null, running = false, hadIdentity = false
  const storageKey = identity => 'rights-inspection:' + identity
  const storageCall = (method, key, value) => { try { if (storage) return storage[method](key, value) } catch (_) { /* Storage can be unavailable in private browsing. */ } }
  const save = () => {
    if (!state.identity) return
    if (state.target) storageCall('setItem', storageKey(state.identity), JSON.stringify(state.target))
    else storageCall('removeItem', storageKey(state.identity))
  }
  const targetValue = target => target && ['person', 'role'].includes(target.kind) && Number.isSafeInteger(Number(target.id)) && Number(target.id) > 0
    ? { kind: target.kind, id: Number(target.id), label: String(target.label || '').slice(0, 300), role_label: String(target.role_label || '').slice(0, 300) } : null
  const invalidate = () => { revision++; state.results = {}; state.error = ''; state.loading = false; if (timer !== null) cancel(timer); timer = null }
  const allChecks = () => {
    const checks = new Map()
    for (const values of registrations.values()) for (const value of values) {
      const check = normalizeInspectionCheck(value)
      if (check) checks.set(inspectionCheckKey(check), check)
    }
    return checks
  }
  const queue = () => {
    if (!state.eligible || !state.target || state.mode === 'preview' || timer !== null || running) return
    timer = schedule(() => { timer = null; flush() })
  }
  async function flush() {
    if (running || !state.eligible || !state.target || state.mode === 'preview') return
    const batch = [...allChecks()].filter(([key]) => state.results[key] === undefined).slice(0, 200)
    if (!batch.length) return
    const version = revision
    const target = { ...state.target }
    running = true; state.loading = true
    try {
      const response = await request({ target: { kind: target.kind, id: target.id },
        checks: batch.map(([, check], index) => ({ ...check, key: 'c' + index })) })
      if (version !== revision || !state.target || !state.eligible) return
      const data = response && response.data ? response.data : response
      if (!data || data.success !== true || !data.results) throw new Error(data && data.message || 'Les droits ne peuvent pas être évalués.')
      for (let index = 0; index < batch.length; index++) {
        set(state.results, batch[index][0], data.results['c' + index] || { allowed: null, error: true, reason: 'Évaluation indisponible.' })
      }
      if (data.target) {
        state.target = { ...state.target, label: data.target.label || state.target.label, role_label: data.target.role_label || state.target.role_label }
        save()
      }
    } catch (error) {
      if (version !== revision || !state.target || !state.eligible) return
      const data = error.response && error.response.data
      state.error = data && data.message || error.message || 'L’inspection des droits a échoué.'
      for (const [key] of batch) set(state.results, key, { allowed: null, error: true, reason: state.error })
    } finally {
      running = false
      if (version === revision) state.loading = false
      queue()
    }
  }
  return {
    state,
    setIdentity(identity) {
      identity = identity || ''
      if (identity === state.identity) return
      const previous = state.identity
      if (previous) storageCall('removeItem', storageKey(previous))
      if (state.mode === 'preview') { state.viewGeneration++; state.transitioning = true }
      invalidate(); state.identity = identity; state.eligible = !!identity; state.target = null; state.mode = 'annotations'; state.viewError = ''
      // Restore on a page refresh only, never when switching accounts/profiles in this runtime.
      if (identity && !hadIdentity) {
        hadIdentity = true
        try { state.target = targetValue(JSON.parse(storageCall('getItem', storageKey(identity)) || 'null')) } catch (_) { state.target = null }
        queue()
      }
    },
    select(target, mode = 'annotations') {
      if (!state.eligible) return false
      const next = targetValue(target)
      if (!next) return false
      const nextMode = next.kind === 'person' && mode === 'preview' ? 'preview' : 'annotations'
      if ((state.mode === 'preview' || nextMode === 'preview')
        && (state.mode !== nextMode || !state.target || state.target.id !== next.id)) {
        state.viewGeneration++; state.transitioning = true; state.viewError = ''
      }
      invalidate(); state.target = next; state.mode = nextMode; save(); queue(); return true
    },
    exit() {
      if (state.mode === 'preview') { state.viewGeneration++; state.transitioning = true; state.viewError = '' }
      invalidate(); state.target = null; state.mode = 'annotations'; save()
    },
    finishViewTransition(generation, error = '') {
      if (generation !== state.viewGeneration) return
      state.transitioning = false; state.viewError = error
    },
    register(owner, checks) { registrations.set(owner, Array.isArray(checks) ? checks : []); queue() },
    unregister(owner) { registrations.delete(owner) },
    result(check) { return state.results[inspectionCheckKey(check)] },
    retry() {
      if (!state.target) return
      if (state.mode === 'preview') { state.viewGeneration++; state.transitioning = true; state.viewError = '' }
      invalidate(); queue()
    },
    flush,
    dispose() { invalidate(); registrations.clear(); state.target = null; state.mode = 'annotations' },
  }
}
